import _ from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import {connect} from 'react-redux';
import orientationChangeEnd from 'orientationchangeend';
import styles from './css/Person.styl';
import Hero from './components/PersonHero';
import GuestBook from './components/GuestBook';
import StickyGbCta from './components/GuestBook/StickyGbCta';
import Obituary from './components/Obituary';
import MainService from './components/MainService';
import ServiceInfo from './components/ServiceInfo';
import ResourcesBlock from './components/ResourcesBlock';
import GalleryTeaser from './components/GalleryTeaser';
import FlowersCta from './components/FlowersCta';
import Questionnaire from './components/Questionnaire';
import {
  bindMethods,
  isInferiorBrowser,
} from '../../shared/utilities/componentHelpers';
import {
  getPageTypeLayoutKey,
  getPageTypeLayoutMap,
  isBatesvillePageType,
  isForestLawnPageType,
  isFuneralHomePageType,
} from '../../shared/utilities/personPageType';
import {sanitizeText} from '../../../appModules/utilities/formatHelpers';
import {
  createGetObjectByKey,
  orderObjectList,
} from '../../shared/utilities/orderHelpers';
import {scrollToTop} from '../../shared/utilities/scrollHelpers';
import {
  formatTitle,
  simpleFormat,
} from '../../../appModules/utilities/formatHelpers';
import {addQueryParams, buildUrl} from '../../shared/utilities/urlHelpers';
import {
  AD_DIGNITY_MEMORIAL,
  FLOWER_TYPE_LEGACY,
  GA_SITENAMES,
} from '../../shared/utilities/constants';
import appConfig from '../../../appConfig/AppConfig';
import {AdControl} from 'legacy-advertising';

const conf = appConfig();
const FLOWERS_DATA_LAYER_MAP = {
  HeadlineButton: {
    action: 'HeadlineButton',
    category: 'DeceasedHeadlineMod',
  },
  ServicesTopLink: {
    action: 'TopLink',
    category: 'FHMod',
  },
  ThankYouButton: {
    action: 'gbthankyou',
    category: 'gbthankyou',
  },
  FlowersCta: {
    action: 'flowers_module',
    category: 'flowers_module',
  },
  ObitModule: {
    action: 'flowers_obit_header',
    category: 'flowers_obit_header',
  },
};

/**
 * Person page module
 * @class   Person
 * @return  {JSX}     Returns the rendered module
 */
export class Person extends React.Component {
  static propTypes = {
    ads: React.PropTypes.object.isRequired,
    location: React.PropTypes.object,
    mutationStatus: React.PropTypes.object,
    person: React.PropTypes.object,
  };

  /**
   * Person React Component constructor
   * @param       {Object}    props   Passed in props
   * @param       {Object}    context Used for flux store
   * @constructs  {Person}
   * @return      {Undefined} Returns nothing
   */
  constructor(props) {
    super(props);

    this.state = {
      isSingleColumn: false,
      gallery: {
        show: false,
        initialId: null,
      },
    };

    const {person, location} = props;
    const {
      affiliates,
      church,
      displayText,
      funeralHomes,
      obitSources,
      obituaries,
      services,
      flowers,
      guestBook,
      personLayout,
    } = person || {};
    const {
      allowGuestBook,
      allowGuestBookPhoto,
      allowGuestBookVideo,
      allowPersonPagePosting,
    } = guestBook || {};
    const {personPageType} = personLayout;

    this.fpData = {};

    const first = _.get(person, 'name.first');
    const last = _.get(person, 'name.last');
    const obitSourcesId = _.get(obitSources, 'displayText')
      ? _.head(obitSources.displayText)
      : null;

    this.allowGallery =
      allowGuestBook &&
      allowGuestBookPhoto &&
      allowGuestBookVideo &&
      allowPersonPagePosting;

    const layoutMapKey = allowGuestBook
      ? _.get(this.props, 'person.personLayout.layout.left[0]', 'Obit')
      : 'Obit';
    const pageTypeLayoutMapKey = getPageTypeLayoutKey(
      personPageType,
      layoutMapKey
    );

    this.layoutMap = getPageTypeLayoutMap(personPageType, pageTypeLayoutMapKey);

    this.getObjectByKey = createGetObjectByKey('sourceId');

    /* Links */
    this.canonicalUrl = buildUrl(
      conf.vanityDomain,
      _.get(location, 'pathname'),
      [
        {key: 'pid', value: _.get(location, 'query.pid')},
        {key: 'sid', value: _.get(location, 'query.sid')},
        {key: 'v', value: _.get(location, 'query.v')},
      ]
    );

    /* Obituaries */
    this.obituaries = orderObjectList(
      obituaries,
      _.get(person, 'obitSources.person'),
      this.getObjectByKey
    );

    const obitsWithPartners = this.addInfoToObituaries(
      this.obituaries,
      affiliates,
      funeralHomes,
      this.getObjectByKey
    );

    this.primaryDisplayTextObit = this.getObjectByKey(
      obitSourcesId,
      obitsWithPartners
    );

    this.displayObituaries =
      _.size(this.obituaries) > 0
        ? this.processObituaries(
            obitsWithPartners,
            isFuneralHomePageType(personPageType),
            this.primaryDisplayTextObit
          )
        : [];

    this.hasPrimaryObit = _.isObject(this.primaryDisplayTextObit);
    this.primaryFuneralHome = this.getObjectByKey(
      this.primaryDisplayTextObit.funeralHomeId,
      funeralHomes
    );

    this.funeralHomeAttribution = this.getFuneralHomeAttribution(
      this.primaryDisplayTextObit,
      this.primaryFuneralHome
    );

    /* Flowers */
    const {fhShowFlowers, showFlowersAndGifts} = personLayout;
    const {link, source, eventTagDateRecieved, eventTagDateDod} = flowers || {};
    const showLegacyFlowerOnPartner = this.shouldDisplayLegacyFlowerOnPartner(
      personPageType,
      source,
      link
    );

    const revShareAffiliateId = showLegacyFlowerOnPartner
      ? _.get(this.primaryDisplayTextObit, 'affiliate.sourceId')
      : null;

    this.showFlowers = showFlowersAndGifts && fhShowFlowers && link;
    this.flowersLinks = this.getFlowersUrls(
      link,
      source,
      showLegacyFlowerOnPartner,
      revShareAffiliateId
    );
    this.flowersDataLayerObjs = this.getDataLayerObjs(
      FLOWERS_DATA_LAYER_MAP,
      eventTagDateRecieved,
      eventTagDateDod,
      this.getDataLayerObj
    );

    /* Text */
    this.showReadMore = _.get(displayText, 'showReadMore', false);
    this.textToDisplay = this.getTextToDisplay(
      personPageType,
      _.get(displayText, 'text'),
      showLegacyFlowerOnPartner
    );

    /* Services */
    this.flowersLocation = this.getFlowersLocation(flowers);
    this.serviceGroup = this.addFlowersLink(
      _.cloneDeep({services, funeralHomes, church}),
      flowers,
      this.flowersLinks.page
    );
    this.hasServices = this.checkServices(this.serviceGroup);

    /* Resources */
    this.resources = this.getFuneralHomeResources(personPageType);

    /* General */
    this.isInferiorBrowser = isInferiorBrowser(
      typeof navigator === 'undefined' ? null : navigator
    );
    this.title = formatTitle(
      first,
      last,
      _.get(location, 'city'),
      _.get(location, 'state')
    );

    bindMethods(this, [
      'flowerClickEvents',
      'getGuestBookPosition',
      'handleResize',
      'handleModalClose',
      'handleModalOpen',
      'openCondolenceEntry',
      'scrollToGuestbook',
      'showGallery',
    ]);

    this.initializeOrientationChange();
  }

  /**
   * Lifecycle method
   * @return {Undefined} Returns nothing
   */
  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('focus', this.handleResize);
    this.handleResize();
  }

  /**
   * Remove event listeners.
   * @return {Undefined} Returns nothing
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('focus', this.handleResize);
  }

  /**
   * Initialize the orientationchangeend custom event
   * @return {Undefined} Returns nothing
   */
  initializeOrientationChange() {
    if (typeof window !== 'undefined') {
      const OCE = orientationChangeEnd();
      let orientationchangeend = null;

      if (!('onorientationchangeend' in window)) {
        window.onorientationchangeend = true;

        orientationchangeend = new CustomEvent('orientationchangeend');

        OCE.on('orientationchangeend', () => {
          window.dispatchEvent(orientationchangeend);
        });
      }
    }
  }

  /**
   * Opens the guestbook condolence entry
   * @return {Undefined} Returns nothing
   */
  openCondolenceEntry() {
    const guestbook = this.refs.guestbook.getWrappedInstance();

    guestbook.openCondolenceEntry();
  }

  /**
   * gets position of guestbook
   * @return {number} top position of guestbook
   */
  getGuestBookPosition() {
    const guestBookRect = ReactDOM.findDOMNode(
      this.refs.guestbook
    ).getBoundingClientRect();
    const offsetTop = guestBookRect.top;

    return offsetTop;
  }

  /**
   * Scrolls to the guestbook entry
   * @return {undefined} returns nothing
   */
  scrollToGuestbook() {
    const scrollSpeed = 300;
    const element = this.isInferiorBrowser
      ? document.documentElement
      : document.body;

    scrollToTop(
      element,
      Math.round(this.getGuestBookPosition() + window.scrollY),
      scrollSpeed,
      this.openCondolenceEntry
    );
  }

  /**
   * Constructs a data layer object based on properties for flowers
   * @param  {Object} eventTag Event Tag object
   * @param  {String} category The event category
   * @param  {String} action The event action
   * @param  {String} type The type of data layer obj
   * @return {Object} Data Layer Obj
   */
  getDataLayerObj(eventTag, category, action, type) {
    const dataLayerObj = {};

    if (eventTag) {
      const {label, value} = eventTag;

      dataLayerObj.event = 'GAEvent';
      dataLayerObj.eventCategory = `ByPage_Person_${category}_${type}`;
      dataLayerObj.eventAction = `SendFlowers_${action}_Click_${type}`;
      dataLayerObj.eventLabel = label ? label : `Person_DataType_null_${type}`;
      if (value) {
        dataLayerObj.eventValue = value;
      }
    }

    return dataLayerObj;
  }

  /**
   * Constructs the data layer objects needed for flower click events
   * @param  {Object}   flowersClickMap Map of the Flower buttons and their basic properties
   * @param  {Object}   eventTagDateRecieved Event Tag object
   * @param  {Object}   eventTagDateDod Event Tag object
   * @param  {Function} getDataLayerObj Data Layer construction function
   * @return {Object}   Object map of data layer object arrays
   */
  getDataLayerObjs(
    flowersClickMap,
    eventTagDateRecieved,
    eventTagDateDod,
    getDataLayerObj
  ) {
    const dataLayerObjs = {};

    _.each(flowersClickMap, (item, key) => {
      dataLayerObjs[key] = [];

      const dataLayerDateReceived = getDataLayerObj(
        eventTagDateRecieved,
        item.category,
        item.action,
        'DateReceived'
      );
      const dataLayerDOD = getDataLayerObj(
        eventTagDateDod,
        item.category,
        item.action,
        'DOD'
      );

      if (!_.isEmpty(dataLayerDateReceived)) {
        dataLayerObjs[key].push(dataLayerDateReceived);
      }

      if (!_.isEmpty(dataLayerDOD)) {
        dataLayerObjs[key].push(dataLayerDOD);
      }
    });

    return dataLayerObjs;
  }

  /**
   * Determines if the legacy flower should display on a Partner obit
   * @param  {String}     personPageType  the person page type
   * @param  {String}     source  the type of flower url potentially available to the page
   * @param  {String}     link  the flowers link url or empty string
   * @return {Undefined}  Returns whether the legacy flower link should be displayed
   */
  shouldDisplayLegacyFlowerOnPartner(personPageType, source, link) {
    const isPageTypeBatesville = isBatesvillePageType(personPageType);
    const isSourceFlowerTypeLegacy = source === FLOWER_TYPE_LEGACY;

    return isPageTypeBatesville && isSourceFlowerTypeLegacy && link;
  }

  /**
   * Pushes flower button click events to GA data layer.
   * @param  {String}     buttonPosition Label for button position.
   * @return {Undefined}  Returns nothing, pushes GA events to the dataLayer.
   */
  flowerClickEvents(buttonPosition) {
    if (this.flowersDataLayerObjs[buttonPosition]) {
      _.each(this.flowersDataLayerObjs[buttonPosition], obj => {
        if (window && window.dataLayer) {
          window.dataLayer.push(obj);
        }
      });
    }
  }

  /**
   * Gets the location for a the flowers link
   * @method getFlowersLocationInformation
   * @param  {Object} flowers Flowers information
   * @return {Object} Returns the flowers location object
   */
  getFlowersLocation(flowers) {
    const {funeralHome, church} = flowers || {};

    return funeralHome || church || null;
  }

  /**
   * Adds flowers links to the appropriate partners
   * @method addFlowersLink
   * @param  {Object} serviceGroup Services, Funeral Homes, and Churches
   * @param  {Object} flowers      Flowers information
   * @param  {String} flowersLink  The page level flowers link
   * @return {Object} Returns an services group that has flowers links
   */
  addFlowersLink(serviceGroup, flowers, flowersLink) {
    const {funeralHome, church} = flowers || {};

    if (funeralHome) {
      serviceGroup.funeralHomes.map(fh => {
        if (fh.sourceId === funeralHome.sourceId) {
          fh.flowersLink = flowersLink;
          return fh;
        }
        return fh;
      });

      serviceGroup.funeralHomes.sort(fh => (fh.flowersLink ? -1 : 1));
    }

    if (church) {
      serviceGroup.church.flowersLink = flowersLink;
    }

    return serviceGroup;
  }

  /**
   * Get the Information to render a Resources Block
   * @method getFuneralHomeResources
   * @param  {String} personPageType the type of person page being rendered
   * @return {Object} Returns an object containing title plus array of resource objects
   */
  getFuneralHomeResources(personPageType) {
    let resources = [];
    let title = 'Advice & Support Resources';

    if (isForestLawnPageType(personPageType)) {
      title = 'Resources';
      resources = [
        {
          type: 'flplanning',
          target: '_blank',
          asset: 'flplanning.jpg',
          text: 'Advanced Planning',
          url: 'https://forestlawn.com/plan-ahead/',
        },
        {
          type: 'flgrief',
          target: '_blank',
          asset: 'flgrief.jpg',
          text: 'Grief Support Services',
          url: 'https://forestlawn.com/grief-resources/',
        },
        {
          type: 'fllocations',
          target: '_blank',
          asset: 'fllocations.jpg',
          text: 'Cemetery Property',
          url: 'https://forestlawn.com/locations/',
        },
      ];
    } else {
      resources = [
        {
          type: 'planning',
          target: '_self',
          asset: 'planning.png',
          text: 'Funeral Pre-Planning',
          url: `${conf.vanityDomain}/funeral-homes/planning`,
        },
        {
          type: 'etiquette',
          target: '_self',
          asset: 'etiquette.png',
          text: 'Funeral & Sympathy Etiquette',
          url: `${
            conf.vanityDomain
          }/news/advice-and-support/article/funeral-etiquette`,
        },
        {
          type: 'grief',
          target: '_self',
          asset: 'grief.png',
          text: 'Coping With Grief',
          url: `${
            conf.vanityDomain
          }/news/advice-and-support/article/grief-support`,
        },
        {
          type: 'obitwriter',
          target: '_self',
          asset: 'obitwriter.png',
          text: 'Preserving Memories Forever',
          url: `${
            conf.vanityDomain
          }/news/advice-and-support/article/how-to-preserve-our-precious-memories-forever`,
        },
      ];
    }
    return {title, resources};
  }

  /**
   * Adds affilate and funeralHome information to obituaries
   * @param {Array}     obituaries The obituaries to loop through
   * @param {Array}     affiliates The affiliates list
   * @param {Array}     funeralHomes The funeralHome list
   * @param {Function}  getObjectByKey The function to fetch items from an object list
   * @return {Array} The mapped list of obituaries with the new info properties
   */
  addInfoToObituaries(obituaries, affiliates, funeralHomes, getObjectByKey) {
    let obitsWithPartner = [];

    if (obituaries) {
      obitsWithPartner = obituaries.map(obituary => {
        const obit = _.cloneDeep(obituary);
        const {affiliateId, funeralHomeId} = obit;

        obit.affiliate = getObjectByKey(affiliateId, affiliates);

        const noAffiliatePresent =
          funeralHomeId > 1 && !_.get(obit, 'affiliate.sourceId');
        const isFuneralHomePublisher =
          _.get(obit, 'affiliate.affiliateType') === 'Funeral Home Publisher';

        if (noAffiliatePresent || isFuneralHomePublisher) {
          obit.funeralHome = getObjectByKey(funeralHomeId, funeralHomes);
        }

        return obit;
      });
    }

    return obitsWithPartner;
  }

  /**
   * Check to see if the service group should be displayed
   * @method checkServices
   * @param  {Object}   serviceGroup Services, Funeral Home, and Church
   * @return {Boolean}  Returns true if any service item is present
   */
  checkServices(serviceGroup) {
    const numberOfServices = _.keys(serviceGroup).length;
    let emptyServices = 0;

    _.mapValues(serviceGroup, item => {
      if (
        _.isEmpty(item) ||
        (!_.isArray(item) && (!item.sourceId || item.sourceId === 1))
      ) {
        emptyServices++;
      }
    });

    return numberOfServices > 0 && emptyServices < numberOfServices;
  }

  /**
   * Constructs the flowers urls for Person and ThankYou components
   * @param   {String} flowersLink The basic flowers link
   * @param   {String} source The source of the flowers link
   * @param   {Boolean} allowLegacyFlower allow the partner obit to display a Legacy flower link
   * @param   {Number} revShareAffiliateId AffiliateId to add to flowers url for rev share
   * @return  {String} Returns the flowers url
   */
  getFlowersUrls(flowersLink, source, allowLegacyFlower, revShareAffiliateId) {
    const urls = {page: '', thankYou: ''};

    if (flowersLink) {
      if (source === FLOWER_TYPE_LEGACY) {
        if (allowLegacyFlower) {
          urls.thankYou = `${flowersLink}&rs=07`;
          urls.page = `${flowersLink}&rs=03`;
        }
      } else {
        urls.thankYou = flowersLink;
        urls.page = flowersLink;
      }

      if (revShareAffiliateId) {
        urls.thankYou = addQueryParams(urls.thankYou, {
          affiliateid: revShareAffiliateId,
        });
        urls.page = addQueryParams(urls.page, {
          affiliateid: revShareAffiliateId,
        });
      }
    }

    return urls;
  }

  /**
   * Gets the funeral home attribution if it's there and conditions are met
   * @param  {Object} primaryObit The primary obit
   * @param  {Object} primaryFuneralHome The primary obit's funeral home
   * @return {String} The funeral home attribution
   */
  getFuneralHomeAttribution(primaryObit, primaryFuneralHome) {
    let attribution = '';
    const hasValidFuneralHome = _.get(primaryObit, 'funeralHomeId', 0) > 1;

    if (primaryObit && hasValidFuneralHome) {
      const isFuneralHomePublisher =
        _.get(primaryObit, 'affiliate.affiliateType') ===
        'Funeral Home Publisher';

      if (!primaryObit.affiliateId || isFuneralHomePublisher) {
        attribution = _.get(primaryFuneralHome, 'name', '');
      }
    }

    return attribution;
  }

  /**
   * Determines how much text to display based on the obit type
   * @method getTextToDisplay
   * @param   {String}  personPageType the type of person page being rendered
   * @param   {String}  text The display text
   * @param   {Boolean}  showFullTeaser Whether to show full text as teaser
   * @return  {String}  Returns the text to display
   */
  getTextToDisplay(personPageType, text, showFullTeaser) {
    let formattedText = text;
    const textToDisplay = {
      teaser: '',
      full: '',
    };
    const MAX_DESC_CHARS = 300;
    const tags = ['blockquote', 'p', 'a', 'ul', 'ol', 'nl', 'li', 'b', 'i', 'strong', 'em', 'strike', 'code', 'hr', 'table', 'thead', 'caption', 'tbody', 'tr', 'th', 'td', 'pre'];

    if (isForestLawnPageType(personPageType)) {
      formattedText = simpleFormat(text);
      tags.push('br');
    }

    const options = {
      length: MAX_DESC_CHARS,
      separator: ' ',
    };

    if (formattedText) {
      textToDisplay.full = sanitizeText(formattedText, tags);

      if (showFullTeaser) {
        textToDisplay.teaser = textToDisplay.full;
      } else if (textToDisplay.full.length <= MAX_DESC_CHARS) {
        textToDisplay.teaser = textToDisplay.full;
      } else {
        textToDisplay.teaser = _.flow(
          _.partial(sanitizeText, textToDisplay.full, []),
          _.partial(_.truncate, _, options)
        )();
      }
    }
    return textToDisplay;
  }

  /**
   * Filter out the obituaries before sending to the source group
   * - return obits that have a partner affiliation
   * - for a partner page, exclude the actual partner obit
   * @param  {Array} obituaries The obituaries to be filtered
   * @param  {Boolean} isPartnerPageType Is the person page is a partner obit page
   * @param  {Object} primaryDisplayTextObit The obit providing the display text
   * @return {Array} The filtered obituaries
   */
  processObituaries(obituaries, isPartnerPageType, primaryDisplayTextObit) {
    return obituaries.filter(obituary => {
      if (
        isPartnerPageType &&
        obituary.sourceId === primaryDisplayTextObit.sourceId
      ) {
        return false;
      }

      const isFuneralHome = !_.isNull(
        _.get(obituary, 'funeralHome.sourceId', null)
      );
      const isAffiliate = !_.isNull(
        _.get(obituary, 'affiliate.sourceId', null)
      );

      return isFuneralHome || isAffiliate;
    });
  }

  /**
   * shows gallery with given id
   * @param  {number} id the initial gallery id
   * @return {undefined} returns nothing
   */
  showGallery(id) {
    this.setState({
      gallery: {
        show: _.isNumber(id),
        initialId: id,
      },
    });
  }

  /**
   * Modal close handler
   * @param {string} id Contains the instruction on what to do with the
   * gallery.
   * @return {Undefined} Returns nothing.
   */
  handleModalClose(id) {
    if (id === 'showGallery') {
      this.showGallery();
    }
  }

  /**
   * Modal open handler
   * @return {Undefined} Returns nothing.
   */
  handleModalOpen() {
    this.setState({displayStickySignGuestBook: false});
  }

  /**
   * resize handler
   * @return {Undefined} Returns nothing
   */
  handleResize() {
    const width = window.outerWidth;
    const singleColumn = 940;

    if (width < singleColumn) {
      if (!this.state.isSingleColumn) {
        this.setState({isSingleColumn: true});
      }
    } else if (this.state.isSingleColumn) {
      this.setState({isSingleColumn: false});
    }
  }

  /**
   * React Component render.
   * @return {JSX} Person page rendered.
   */
  render() {
    const layout = {
      map: this.layoutMap,
      columns: this.state.isSingleColumn ? 'single' : 'dual',
    };
    const {
      person: {
        personLayout: {personPageType},
      },
    } = this.props;
    const firstToFile = _.get(
      this.props,
      'person.customDimensions.firstToFile'
    );
    const obituary = (
      <Obituary
        firstName={_.get(this.props, 'person.name.first', '')}
        flowerClickEvents={_.partial(this.flowerClickEvents, 'ObitModule')}
        flowersLink={this.flowersLinks.page}
        funeralHomeAttribution={this.funeralHomeAttribution}
        hasPrimaryObit={this.hasPrimaryObit}
        parentLayout={layout}
        personPageType={personPageType}
        primaryDisplayTextObit={this.primaryDisplayTextObit}
        primaryFuneralHome={this.primaryFuneralHome}
        showFlowers={this.showFlowers}
        showReadMore={this.showReadMore}
        sourceGroup={
          _.size(this.displayObituaries) > 0 ? this.displayObituaries : null
        }
        textToDisplay={this.textToDisplay}
      />
    );
    const guestbook = (
      <GuestBook
        allowGallery={this.allowGallery}
        canonicalUrl={this.canonicalUrl}
        count={_.get(this.props, 'relay.variables.count')}
        flowersLink={this.flowersLinks.thankYou}
        flowersLocation={this.flowersLocation}
        fpData={this.fpData}
        galleryConfig={this.state.gallery}
        handleModalClose={this.handleModalClose}
        handleModalOpen={this.handleModalOpen}
        isInferiorBrowser={this.isInferiorBrowser}
        location={this.props.location}
        primaryFuneralHome={this.primaryFuneralHome}
        ref="guestbook"
        thankYouFlowerClickEvents={_.partial(
          this.flowerClickEvents,
          'ThankYouButton'
        )}
        title={this.title}
      />
    );
    const mainService = (
      <MainService
        flowerClickEvents={_.partial(this.flowerClickEvents, 'ServicesTopLink')}
        flowersLink={this.flowersLinks.page}
        personPageType={personPageType}
        serviceGroup={this.serviceGroup}
        showFlowers={this.showFlowers}
      />
    );
    const serviceInfo = <ServiceInfo serviceGroup={this.serviceGroup} />;
    const resourcesBlock =
      this.resources.resources.length > 0 ? (
        <ResourcesBlock
          resources={this.resources.resources}
          title={this.resources.title}
        />
      ) : null;
    const gallery = (
      <GalleryTeaser
        allowGallery={this.allowGallery}
        count={_.get(this.props, 'person.guestBook.totalMediaCount')}
        isSingleColumn={this.state.isSingleColumn}
        items={
          _.chunk(
            _.get(this.props, 'person.guestBookGallery.condolences.edges', []),
            8
          )[0]
        }
        openCondolenceEntry={this.scrollToGuestbook}
        showGalleryHandler={this.showGallery}
      />
    );
    const flowers = (
      <FlowersCta
        flowerClickEvents={_.partial(this.flowerClickEvents, 'FlowersCta')}
        flowersLink={this.flowersLinks.page}
        personPageType={personPageType}
        showFlowers={this.showFlowers}
      />
    );
    const components = {
      obituary,
      guestbook,
      mainService,
      serviceInfo,
      resourcesBlock,
      gallery,
      flowers,
    };

    return (
      <div className={styles.Person}>
        <StickyGbCta
          display={this.state.displayStickySignGuestBook}
          getGuestBookPosition={this.getGuestBookPosition}
          guestBookHandler={this.scrollToGuestbook}
          hasSubmitted={_.has(this.props.mutationStatus, 'condolence')}
          isSingleColumn={this.state.isSingleColumn}
        />
        <Hero
          canonicalUrl={this.canonicalUrl}
          flowerClickEvents={_.partial(
            this.flowerClickEvents,
            'HeadlineButton'
          )}
          flowersLink={this.flowersLinks.page}
          funeralHomeAttribution={this.funeralHomeAttribution}
          galleryCount={_.get(this.props, 'person.guestBook.totalMediaCount')}
          isSingleColumn={this.state.isSingleColumn}
          person={this.props.person}
          personPageType={personPageType}
          scrollToGuestbook={this.scrollToGuestbook}
          showGalleryHandler={this.showGallery}
          title={this.title}
        />
        <div className={styles.personContent}>
          {this.state.isSingleColumn ? (
            <main>
              {this.layoutMap.single.map((component, key) => (
                <div key={key}>{components[component]}</div>
              ))}
            </main>
          ) : (
            <main>
              <div className={styles.left}>
                {this.layoutMap.dual.left.map((component, key) => (
                  <div key={key}>{components[component]}</div>
                ))}
              </div>
              <div className={styles.right}>
                {this.layoutMap.dual.right.map((component, key) => (
                  <div key={key}>{components[component]}</div>
                ))}
              </div>
            </main>
          )}
        </div>
        {firstToFile === GA_SITENAMES.dignityMemorial ? (
          <AdControl
            adService={this.props.ads.adService}
            adSize={AD_DIGNITY_MEMORIAL.adSize}
            clientLoadOnly={AD_DIGNITY_MEMORIAL.clientLoadOnly}
            maxIdleAdRefreshes={AD_DIGNITY_MEMORIAL.maxIdleAdRefreshes}
            pageName={AD_DIGNITY_MEMORIAL.pageName}
            positionId={AD_DIGNITY_MEMORIAL.positionId}
            slotName={AD_DIGNITY_MEMORIAL.slotName}
          />
        ) : null}
        {_.get(this.props, 'person.showUGC') ? <Questionnaire /> : null}
      </div>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ads: state.adStore,
  mutationStatus: _.get(state, 'personStore.mutationStatus'),
  person: _.get(state, 'personStore.person'),
});

export default connect(mapStateToProps)(Person);



// WEBPACK FOOTER //
// ./src/reactModules/pages/Person/Person.js